import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import api from '../api/api';
import '../styles/AdminEditCenterPage.css';

const AdminEditCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [center, setCenter] = useState({
    name: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    whatsapp: '',
    facebook: '',
    logoUrl: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    // Check if user has permission to edit center
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    // Fetch admin's center data
    if (user.center) {
      fetchCenterData(user.center);
    } else {
      setError('No center associated with your account');
      setLoading(false);
    }
  }, [user, navigate]);

  const fetchCenterData = async (centerId) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/centers/${centerId}`);
      
      if (response.data) {
        const centerData = response.data.success ? response.data.data : response.data;
        
        setCenter({
          name: centerData.name || '',
          address: centerData.address || '',
          contactEmail: centerData.contactEmail || centerData.email || '',
          contactPhone: centerData.contactPhone || centerData.phone || '',
          website: centerData.website || '',
          whatsapp: centerData.whatsapp || '',
          facebook: centerData.facebook || '',
          logoUrl: centerData.logoUrl || null
        });
        
        // If center has a logo, set the preview
        if (centerData.logoUrl) {
          setLogoPreview(centerData.logoUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching center data:', err);
      setError('Failed to load center data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCenter(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!center.name || !center.contactEmail) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(center.contactEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      const formData = new FormData();
      formData.append('name', center.name);
      formData.append('address', center.address || '');
      formData.append('contactEmail', center.contactEmail);
      formData.append('contactPhone', center.contactPhone || '');
      formData.append('website', center.website || '');
      formData.append('whatsapp', center.whatsapp || '');
      formData.append('facebook', center.facebook || '');
      
      // If there's a new logo file, append it
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      const response = await api.put(`/centers/${user.center}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        console.log('Update successful:', response.data);
        setSuccess(true);
        
        // Navigate back to settings after success
        setTimeout(() => {
          navigate('/settings');
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating center:', err);
      setError(`Failed to update center information: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-edit-center-container">
      <div className="header">
        <div className="header-left">
          <Link to="/settings" className="back-link">
            &larr; Back to Settings
          </Link>
          <h1>Edit Center Information</h1>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading center data...</p>
        </div>
      ) : (
        <div className="admin-edit-center-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">Center information updated successfully!</div>}
          
          <form onSubmit={handleSubmit} className="center-form">
            <div className="form-sections">
              <div className="form-section logo-section">
                <h2>Center Logo</h2>
                <div className="logo-container">
                  <div className="logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Center Logo" className="logo-image" />
                    ) : (
                      <div className="logo-placeholder">
                        <i className="placeholder-icon">📷</i>
                        <span>No Logo</span>
                      </div>
                    )}
                  </div>
                  <label className="logo-upload-button">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange} 
                      className="file-input"
                    />
                  </label>
                </div>
              </div>

              <div className="form-section center-details">
                <h2>Center Details</h2>
                
                <div className="form-group">
                  <label htmlFor="name">Center Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={center.name}
                    onChange={handleInputChange}
                    placeholder="Enter center name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={center.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contactEmail">Contact Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={center.contactEmail}
                    onChange={handleInputChange}
                    placeholder="Enter contact email"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contactPhone">Contact Phone</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={center.contactPhone}
                    onChange={handleInputChange}
                    placeholder="Enter contact phone"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={center.website}
                    onChange={handleInputChange}
                    placeholder="e.g. https://yourwebsite.org or www.yourwebsite.org"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="whatsapp">WhatsApp</label>
                  <input
                    type="text"
                    id="whatsapp"
                    name="whatsapp"
                    value={center.whatsapp}
                    onChange={handleInputChange}
                    placeholder="e.g. +447957006983"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="facebook">Facebook</label>
                  <input
                    type="url"
                    id="facebook"
                    name="facebook"
                    value={center.facebook}
                    onChange={handleInputChange}
                    placeholder="e.g. https://facebook.com/pagename"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => navigate('/settings')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-button"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminEditCenterPage;